import { Component, EventEmitter, Input, OnDestroy, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MembersService } from '../../services/members.service';
import { RoleService } from '../../services/role.service';
import { Role } from '../../model/role.model';
import { Workspace, ProjectSwitches } from '../../services/workspaces.service';
import {
  Control, doorsInto, seatsAreFull, BUILDERS_NEEDS_DEV_MODE,
  USERNAME_SEAT_FULL, USERNAME_SEAT_FULL_INVITE_SUGGESTION,
  ADMIN_GRANT_TITLE, adminGrantBody, emailDraftError,
} from '../../services/workspaces.util';
import { generatePassword } from './password.util';
import type { Prefilled } from '../../services/mcp/actions/confirm-lane';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';

export type AddPersonMode = 'email' | 'username';

/** `admin` — offered on a username add only. An invitation can never carry it: a link can be
 *  forwarded, so whoever accepts it would become admin (docs/adr/0006 in olympus-functions;
 *  `inviteUser` refuses it). A username add sends no link, and asks for confirmation first. */
const ADMIN = 'admin';

/**
 * "Add someone" — Task 9. Lives INSIDE the expanded workspace row, above the people list, because
 * the workspace is the row you are in, not a dropdown to pick — that is the whole point of putting
 * this here instead of reusing the old console's global "Add user" form (user-mgmt.component.ts).
 *
 * Two modes, one door each:
 *  - `email`    → `MembersService.addUser({ mode: 'email', ... })` → `inviteUser`.
 *  - `username` → `MembersService.addUser({ mode: 'username', ... })` → `manageUserAccount`.
 *
 * `addUser` already picks the transport by `mode` (members.service.ts:211) — this component never
 * calls `inviteUser`/`manageUserAccount` directly.
 */
@Component({
  selector: 'app-add-person',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './add-person.component.html',
  styleUrl: './add-person.component.css',
})
export class AddPersonComponent implements OnDestroy {
  @Input() workspace!: Workspace;
  /** The PROJECT-wide switches (from `WorkspacesTabComponent.switches`, plumbed through the row) —
   *  distinct from `workspace.invitesEnabled`/`addsEnabled`, which are this ONE workspace's own.
   *  Invitations can be closed at either level; direct adds only at the workspace level. Null while
   *  the tab's own first load hasn't resolved yet — treated as "not disabled" rather than blocking
   *  the form on a still-loading fact. */
  @Input() switches: ProjectSwitches | null = null;
  /** Whether Developer mode is on — the THIRD door (fix-round-1 finding 4): both transports refuse
   *  ANY arrival into `workspace.isBuilders` while this is off, ahead of either switch above. Missing
   *  reads as off, same as `switches` reads as "not disabled" — see `doorsInto`'s own comment for why
   *  the two default in opposite directions (one fails open, this one fails closed). */
  @Input() developerModeOn = false;
  /** Reports the MODE that just succeeded, not just that something did — fix-round-1 finding 6: the
   *  row's `onAdded` needs to know whether a seat just moved (username, immediately) or not (email,
   *  only at acceptance) to avoid reloading the same two lists twice. */
  @Output() added = new EventEmitter<AddPersonMode>();

  private members = inject(MembersService);
  private roleService = inject(RoleService);
  private waits = inject(ConfirmWaitsNgService);
  /** Claude filled this form and is waiting on the builder's press; only then does the button carry the mark or the form report. */
  pressing = signal(false);

  open = signal(false);
  mode = signal<AddPersonMode>('email');

  email = '';
  username = '';
  /** `type="text"`, deliberately, in the template — not a slip. The builder has to read this back to
   *  the person they're creating it for (there is no invite link in this mode), which a masked field
   *  would make impossible. The old console's own add-user form does the same. */
  password = '';
  roleId = '';

  roles = signal<Role[]>([]);
  rolesLoaded = false;
  rolesLoading = signal(false);

  submitting = signal(false);
  error = signal('');
  /** ws 9a W5 — a neutral note from a successful add (e.g. "<email> is in another workspace …"); shown with a Done button. */
  readonly notice = signal('');

  ADMIN_GRANT_TITLE = ADMIN_GRANT_TITLE;
  adminGrantBody = adminGrantBody;
  confirmingAdmin = signal(false);

  /** Every submit goes through here: adding someone AS admin asks first (owner, 2026-09-25). */
  requestSubmit(): void {
    if (this.submitting() || !this.canSubmit()) return;
    if (this.effectiveMode() === 'username' && this.roleId === ADMIN) { this.confirmingAdmin.set(true); return; }
    void this.submit();
  }
  cancelAdminAdd(): void { this.confirmingAdmin.set(false); }
  async confirmAdminAdd(): Promise<void> {
    this.confirmingAdmin.set(false);
    await this.submit();
  }

  /** "Copied" shows for a moment after the copy button is pressed, then the icon comes back. */
  copied = signal(false);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Fills the password field with a strong random one — the builder still has to hand it over, which
   * is why the field is plain text (see `password`'s own comment) and why Copy sits right beside it.
   * Letters and digits only, with the look-alikes (0/O, 1/l/I) left out, because somebody is going to
   * read it out to somebody else. Twelve of them is far past the server's six-character floor.
   */
  generatePassword(): void {
    if (this.submitting()) return;
    this.password = generatePassword();
    this.error.set('');
  }

  async copyPassword(): Promise<void> {
    if (!this.password) return;
    try {
      await navigator.clipboard.writeText(this.password);
      this.copied.set(true);
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copied.set(false), 1600);
    } catch {
      // The clipboard refused (permissions, an insecure origin). Nothing is lost — the password is on
      // screen in plain text, which is what this field is for.
    }
  }

  ngOnDestroy(): void {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    // The row collapsing or going takes the form with it, so Claude's wait ends here rather than running out.
    this.endWait();
  }

  /** The one door-decision function for this page (`workspaces.util.ts`, fix-round-1 finding 5) —
   *  covers the project switch, the workspace switch, and the builders'-workspace/Developer-mode
   *  case. Nothing here re-decides any of those three facts. */
  doors(): { invite: Control; add: Control } {
    return doorsInto(this.workspace, this.switches, { developerModeOn: this.developerModeOn });
  }
  canInvite(): boolean {
    return this.doors().invite.enabled;
  }
  canAdd(): boolean {
    return this.doors().add.enabled;
  }
  inviteBlockedReason(): string {
    return this.doors().invite.reason;
  }
  addsBlockedReason(): string {
    return this.doors().add.reason;
  }
  bothClosed(): boolean {
    return !this.canInvite() && !this.canAdd();
  }
  /** Both reasons, deduplicated — the builders'-workspace/Developer-mode case gives `invite` and
   *  `add` the identical sentence, and fix-round-1 finding 3 was exactly this: a canned "this
   *  workspace" line that stayed on screen even when the real reason was the project switch. This
   *  reads whatever `doorsInto` actually decided instead. */
  bothClosedReasons(): string[] {
    const d = this.doors();
    return Array.from(new Set([d.invite.reason, d.add.reason].filter(Boolean)));
  }
  /** The "switches above" pointer only makes sense for the two on-screen toggles — there is nothing
   *  "above" to turn on for the Developer-mode case, which lives in the settings overlay and is
   *  Zavo-team-only besides. Detected by reason identity rather than re-deriving the condition. */
  bothClosedIsDeveloperMode(): boolean {
    return this.doors().invite.reason === BUILDERS_NEEDS_DEV_MODE;
  }

  /** The mode actually rendered: the segmented control's own choice when both doors are open, and
   *  whichever ONE door is open otherwise — so a stale `mode` selection (say, the workspace's own
   *  `addsEnabled` flips off while `mode` is still 'username') can never point at a hidden control. */
  effectiveMode(): AddPersonMode {
    if (this.canInvite() && this.canAdd()) return this.mode();
    return this.canInvite() ? 'email' : 'username';
  }

  /** Switching modes clears the error (fix-round-1 finding 7) — a failed email invite must not sit
   *  above the buttons describing a request that is no longer the one about to be sent once the
   *  builder has moved to the username fields. */
  setMode(m: AddPersonMode): void {
    this.mode.set(m);
    this.error.set('');
    if (m === 'email' && this.roleId === ADMIN) this.roleId = '';
  }

  /** The roles each mode may offer — admin only by username (ADR 0006). */
  rolesFor(mode: AddPersonMode): Role[] {
    return mode === 'username' ? this.roles() : this.roles().filter((r) => r.id !== ADMIN);
  }

  /** A full workspace still gets the EMAIL form — a seat is taken at ACCEPTANCE, never by an
   *  outstanding invitation (`seatsAreFull`, workspaces.util.ts), so sending one into a full
   *  workspace is legitimate. This does not extend to the username mode — see `usernameSeatControl`. */
  showFullWarning(): boolean {
    return this.effectiveMode() === 'email' && seatsAreFull(this.workspace);
  }

  /**
   * The username mode's own seat check (fix-round-1 finding 2 — the brief was wrong to assume a
   * 409 surfacing in place was good enough here). Unlike an invitation, a username/password account
   * is created immediately, and the server checks the seat BEFORE creating anything — so a full
   * workspace makes this arm certain to fail, not merely uncertain. The server's own refusal
   * ("This workspace is full. Ask your administrator to make room, then open this link again.") is
   * written for someone ACCEPTING a link — a builder creating an account directly is very often the
   * administrator themselves, being told to ask themselves and re-open a link they never opened. So
   * this is caught here, before the request, with a message addressed to the person actually looking
   * at this form.
   *
   * The sentence itself lives in `workspaces.util.ts` now (fix-round-2 finding 1 — it shipped
   * hardcoded here, which was exactly the copy-drift finding 5 existed to prevent). The "invite by
   * email instead" suggestion is appended only when `doors().invite` is actually open (fix-round-2
   * finding 2): full workspace + invitations off (project or workspace) + adds on is a real
   * combination — the segmented control hides itself, only this arm renders, and pointing at a
   * door that is both shut AND not on screen would be actively misleading. Read from `doors()`,
   * the one call already in this file, rather than a second copy of the door logic.
   */
  usernameSeatControl(): Control {
    if (!seatsAreFull(this.workspace)) return { enabled: true, reason: '' };
    const suggestion = this.doors().invite.enabled ? USERNAME_SEAT_FULL_INVITE_SUGGESTION : '';
    return { enabled: false, reason: USERNAME_SEAT_FULL + suggestion };
  }

  canSubmit(): boolean {
    if (this.effectiveMode() === 'email') return !!this.email.trim();
    return !!this.username.trim() && this.password.length >= 6 && this.usernameSeatControl().enabled;
  }

  openForm(): void {
    this.open.set(true);
    this.mode.set(this.canInvite() ? 'email' : 'username');
    if (!this.rolesLoaded) this.loadRoles();
  }

  closeForm(): void {
    if (this.submitting()) return;
    this.endWait();
    this.notice.set('');
    this.open.set(false);
    this.resetFields();
  }

  private resetFields(): void {
    this.email = '';
    this.username = '';
    this.password = '';
    this.roleId = '';
    this.error.set('');
  }

  /** Same lazy-once-per-row-lifetime discipline as `WorkspaceRowComponent.loadUsers`/`loadInvitations`
   *  — fetched on first open, not on construction, since most rows are never expanded. `query(...,
   *  false)` mirrors `roles-tab.component.ts`'s own call: it returns the list without also pushing it
   *  into `RoleService.$values`, which this component has no subscriber for anyway. */
  private endWait(): void {
    if (this.pressing()) this.waits.report('add-person', { outcome: 'discarded' });
    this.pressing.set(false);
  }

  /** Whether the builder has typed anything here that Claude must not overwrite and that leaving would lose. */
  holdsTyping(): boolean {
    return this.open() && !!(this.email.trim() || this.username.trim() || this.password);
  }

  /**
   * Fills this form for the builder's own press, by this form's own rules. The password goes on screen, because the
   * builder hands it over, and never into the answer: `also` says only where it came from.
   */
  async prefill(values: Record<string, unknown>): Promise<Prefilled> {
    const unchanged = 'Nothing was changed.';
    if (this.submitting()) return { refused: `Someone is being added to ${this.workspace.name}. ${unchanged}` };
    if (this.pressing() || this.holdsTyping()) return { refused: `The builder has the add form open with something in it; ask them to finish or cancel it first. ${unchanged}` };
    const email = String(values['email'] ?? '').trim();
    const username = String(values['username'] ?? '').trim();
    if (email && username) return { refused: `Someone is added by email or by username, not both. ${unchanged}` };
    const mode = values['mode'] ?? (email ? 'email' : username ? 'username' : '');
    if (mode !== 'email' && mode !== 'username') return { refused: `Adding someone needs an email, or a username and a password. ${unchanged}` };

    const door = mode === 'email' ? this.doors().invite : this.doors().add;
    if (!door.enabled) return { refused: `${door.reason} ${unchanged}` };
    let password = '';
    let passwordFrom: 'given' | 'generated' | undefined;
    if (mode === 'email') {
      if (username || values['password']) return { refused: `An invitation carries no username or password: they choose their own sign-in. ${unchanged}` };
      const bad = emailDraftError(email);
      if (bad) return { refused: `${bad} ${unchanged}` };
    } else {
      if (email) return { refused: `A username add carries no email. ${unchanged}` };
      if (!username) return { refused: `A username add needs a username. ${unchanged}` };
      const seat = this.usernameSeatControl();
      if (!seat.enabled) return { refused: `${seat.reason} ${unchanged}` };
      const given = values['password'];
      if (given !== undefined && (typeof given !== 'string' || given.length < 6)) return { refused: `A password holds at least 6 characters. ${unchanged}` };
      password = (given as string | undefined) ?? generatePassword();
      passwordFrom = given === undefined ? 'generated' : 'given';
    }

    if (!this.rolesLoaded) {
      try { await this.loadRoles(); } catch { return { refused: `The roles didn't load. ${unchanged}` }; }
    }
    const roleId = String(values['roleId'] ?? '').trim();
    if (roleId === ADMIN && mode === 'email') return { refused: `An invitation can't make someone an admin, because a link can be forwarded. Add them by username, or invite them and change their role after. ${unchanged}` };
    const wanted = roleId.toLowerCase();
    const role = this.rolesFor(mode).find((r) => r.id === roleId) ?? this.rolesFor(mode).find((r) => String(r.name ?? '').toLowerCase() === wanted);
    if (roleId && !role) return { refused: `This project has no role ${roleId} to add them with; read_screen lists the roles. ${unchanged}` };

    this.open.set(true);
    this.mode.set(mode);
    this.error.set('');
    this.notice.set('');
    this.email = email;
    this.username = username;
    this.password = password;
    this.roleId = role?.id ?? '';
    this.pressing.set(true);
    return { button: mode === 'email' ? 'Send invitation' : 'Create account', ...(passwordFrom ? { also: { passwordFrom } } : {}) };
  }

  private async loadRoles(): Promise<void> {
    this.rolesLoading.set(true);
    try {
      this.roles.set(await this.roleService.query(1000, false));
      this.rolesLoaded = true;
    } finally {
      this.rolesLoading.set(false);
    }
  }

  /**
   * On success: clear the form, close it, `emit added` (carrying which mode just succeeded — see the
   * `@Output`'s own comment) — the row reloads what actually needs reloading for that mode, and
   * re-emits its own `changed` so the tab re-runs `list`. This component never reloads anything
   * itself; it only reports that something changed.
   *
   * On failure: the server's own message, in place, above the buttons — never replaced with a
   * generic one.
   */
  async submit(): Promise<void> {
    if (this.submitting() || !this.canSubmit()) return;
    this.submitting.set(true);
    this.error.set('');
    const mode = this.effectiveMode();
    const reporting = this.pressing();
    // Copied before the form clears itself, because a wait answers Claude with what was added, and never the password.
    const sent = { workspaceId: this.workspace.id, mode, ...(mode === 'email' ? { email: this.email.trim() } : { username: this.username.trim() }), roleId: this.roleId };
    const typedPassword = this.password;
    if (reporting) this.waits.saving('add-person');
    const a = await this.members.addUser({
      mode,
      email: mode === 'email' ? this.email.trim() : undefined,
      username: mode === 'username' ? this.username.trim() : undefined,
      password: mode === 'username' ? this.password : undefined,
      roleId: this.roleId,
      where: { tenantId: this.workspace.id },
    });
    this.submitting.set(false);
    if (!a.ok) {
      this.error.set(a.error || 'Could not add them.');
      if (reporting) this.waits.report('add-person', { outcome: 'failed', reason: typedPassword ? this.error().split(typedPassword).join('[password]') : this.error() });
      return;
    }
    if (reporting) {
      this.pressing.set(false);
      this.waits.report('add-person', { outcome: 'saved', saved: { ...sent, ...(a.notice ? { notice: a.notice } : {}) } });
    }
    // ws 9a W5: with a notice, keep the form open showing it; "Done" (dismissNotice) closes and clears it.
    // The list is told right away either way, so the new invitation row is already there.
    this.notice.set(a.notice ?? '');
    this.resetFields();
    // Was (2026-10-06, ws 9a W5): this.open.set(false);
    if (!this.notice()) this.open.set(false);
    this.added.emit(mode);
  }

  dismissNotice(): void {
    this.notice.set('');
    this.open.set(false);
  }
}
